import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Search as SearchIcon } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const CATS = [
  { slug: "", name: "All Products" },
  { slug: "electronics", name: "Electronics" },
  { slug: "wearables", name: "Wearables" },
  { slug: "apparel", name: "Apparel" },
  { slug: "smart-home", name: "Smart Home" },
  { slug: "travel-gear", name: "Travel Gear" },
  { slug: "modern-office", name: "Modern Office" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState(search);

  useEffect(() => { setTerm(search); }, [search]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (search) qs.set("search", search);
    if (sort && sort !== "featured") qs.set("sort", sort);
    api.get(`/products?${qs.toString()}`).then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
  }, [category, search, sort]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(params);
    if (slug) p.set("category", slug); else p.delete("category");
    setParams(p);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (term) p.set("search", term); else p.delete("search");
    setParams(p);
  };

  const title = useMemo(() => CATS.find((c) => c.slug === category)?.name || "All Products", [category]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-2 font-mono text-xs uppercase tracking-widest text-[#00F0FF]">Catalog</div>
      <h1 className="font-display text-3xl font-bold text-white sm:text-4xl" data-testid="shop-title">{search ? `Results for "${search}"` : title}</h1>

      <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c.slug || "all"} onClick={() => setCategory(c.slug)} data-testid={`filter-${c.slug || "all"}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${category === c.slug ? "bg-[#00F0FF] text-[#08090E]" : "border border-white/10 bg-white/5 text-slate-300 hover:border-[#00F0FF]/40"}`}>
              {c.name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <form onSubmit={submitSearch} className="relative w-48">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search…" className="rounded-full border-white/10 bg-white/5 pl-9 text-sm" data-testid="shop-search-input" />
          </form>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-44 rounded-full border-white/10 bg-white/5 text-sm text-slate-200" data-testid="sort-select"><SlidersHorizontal className="mr-1 h-4 w-4" /><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#111420] text-slate-200">
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
              <SelectItem value="rating">Top Rated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="dw-card h-80 animate-pulse rounded-2xl" />)}
          </div>
        ) : products.length === 0 ? (
          <div className="dw-card rounded-2xl p-16 text-center text-slate-400" data-testid="shop-empty">No products found. Try a different category or search.</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="products-grid">
            {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
